/**
 * Minimal inline markdown renderer. Handles:
 *   - `code` (backtick inline code)
 *   - **bold** (__bold__)
 *   - *italic* (_italic_)
 *   - [links](url)
 *   - Line breaks
 *
 * NOT a full markdown parser — just enough so Claude's responses look right
 * in the chat panel without pulling in a heavy dependency.
 */

type Props = { text: string; className?: string }

export function Markdown({ text, className }: Props) {
  const parts = parse(text)
  return <span className={className}>{parts}</span>
}

function parse(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = []
  let key = 0

  // Split by code blocks first, then process inline formatting
  const segments = text.split(/(`[^`]+`)/)
  for (const segment of segments) {
    if (segment.startsWith('`') && segment.endsWith('`')) {
      nodes.push(
        <code
          key={key++}
          className="rounded-md bg-muted px-1 py-0.5 font-mono text-xs text-foreground"
        >
          {segment.slice(1, -1)}
        </code>,
      )
    } else {
      // Process bold, italic, links within non-code segments
      nodes.push(...parseInline(segment, key))
      key += 10
    }
  }

  return nodes
}

function parseInline(text: string, startKey: number): React.ReactNode[] {
  let key = startKey
  const nodes: React.ReactNode[] = []

  // Replace **bold** and *italic* and [link](url)
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index))
    }
    const token = match[0]
    if (token.startsWith('**') && token.endsWith('**')) {
      nodes.push(
        <strong key={key++} className="font-semibold">
          {token.slice(2, -2)}
        </strong>,
      )
    } else if (token.startsWith('*') && token.endsWith('*')) {
      nodes.push(
        <em key={key++} className="italic">
          {token.slice(1, -1)}
        </em>,
      )
    } else if (token.startsWith('[')) {
      const linkMatch = token.match(/\[([^\]]+)\]\(([^)]+)\)/)
      if (linkMatch) {
        nodes.push(
          <a
            key={key++}
            href={linkMatch[2]}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline underline-offset-4 hover:text-volt-ink"
          >
            {linkMatch[1]}
          </a>,
        )
      }
    }
    lastIndex = match.index + token.length
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }

  return nodes
}
